import { useMemo } from 'react'

import { formatInt, formatPct, pctOf } from '../../lib/format.ts'
import { REJECTION_REASONS } from '../../lib/sse/types.ts'
import type { RejectionBucket } from '../../lib/sse/types.ts'
import { Metric } from '../primitives/Metric.tsx'
import { Panel } from '../primitives/Panel.tsx'

const HUMAN: Record<string, string> = {
  auth_failed: 'credencial recusada',
  rate_limited: 'limite de taxa excedido',
  invalid_endpoint_id: 'endpoint_id malformado',
  endpoint_id_mismatch: 'endpoint_id não confere com o agente',
  invalid_event_type: 'tipo de evento desconhecido',
  empty_payload: 'corpo vazio',
  payload_too_large: 'corpo acima do limite',
  payload_not_json: 'corpo não é JSON',
  missing_event_time: 'sem carimbo de tempo',
  invalid_event_time: 'carimbo de tempo inválido',
  unsupported_schema_version: 'versão de schema não suportada',
}

/** The one rejection reason that is a security finding rather than a capacity one. */
const SECURITY_REASON = 'auth_failed'

/**
 * Motivos que ainda não podem ocorrer, e por quê.
 *
 * `rate_limited` está no contrato e reporta zero hoje: não existe limitador de
 * taxa na ingestão. O zero é informação — sem a nota, um operador olharia para a
 * linha zerada e concluiria que ninguém está sendo limitado, quando na verdade
 * nada limita.
 */
const NOT_YET_IMPLEMENTED: Record<string, string> = {
  rate_limited: 'Sem limitador de taxa.',
}

interface Row extends RejectionBucket {
  /** True para motivo fora do catálogo — renderiza, mas sem rótulo humano. */
  uncatalogued: boolean
}

/**
 * Rejections in the trailing hour.
 *
 * Duas decisões que a lista carrega:
 *
 * **`auth_failed` sai da lista e vira um bloco.** `payload_not_json` descreve um
 * cliente mal configurado; um `auth_failed` subindo descreve credenciais sendo
 * *testadas*, que é assinatura de chave vazada. Ranquear os onze motivos por
 * contagem enterraria essa distinção no instante em que um tenant barulhento
 * passasse a contagem dele.
 *
 * **A ordem é a do contrato, não a da contagem.** O servidor manda sempre os
 * onze motivos, zerados inclusive, e a lista os desenha na mesma ordem sempre.
 * Ordenar por contagem faria as linhas trocarem de lugar a cada 2 s conforme o
 * ataque do momento — e um operador que decorou onde fica `payload_too_large`
 * perderia essa memória. Zerados ficam apagados; o olho vai nos que não são.
 */
export function RejectionsPanel({
  rejections,
  rejectedPerSec,
  className,
}: {
  rejections: readonly RejectionBucket[]
  rejectedPerSec: number | null
  className?: string
}) {
  const { auth, others, total } = useMemo(() => {
    const byReason = new Map(rejections.map((bucket) => [bucket.reason, bucket.count]))
    let sum = 0
    for (const bucket of rejections) sum += bucket.count

    // O catálogo primeiro, na ordem do contrato; motivos fora dele vão para o
    // fim, com o texto cru. Um motivo novo no backend precisa aparecer na tela
    // sem release do front — some seria a falha grave.
    const catalogued: Row[] = REJECTION_REASONS.filter(
      (reason) => reason !== SECURITY_REASON,
    ).map((reason) => ({
      reason,
      count: byReason.get(reason) ?? 0,
      uncatalogued: false,
    }))

    const extra: Row[] = rejections
      .filter(
        (bucket) =>
          bucket.reason !== SECURITY_REASON &&
          !REJECTION_REASONS.some((reason) => reason === bucket.reason),
      )
      .map((bucket) => ({ ...bucket, uncatalogued: true }))

    return {
      auth: byReason.get(SECURITY_REASON) ?? null,
      others: [...catalogued, ...extra],
      total: sum,
    }
  }, [rejections])

  const authShare = auth === null ? 0 : pctOf(auth, total)

  return (
    <Panel
      title="Rejeições (última hora)"
      className={className}
      meta={
        <span>
          total <span className="tnum">{formatInt(total)}</span>
        </span>
      }
    >
      <div className="flex h-full flex-col gap-3">
        <div
          className="border-l-2 border-crit py-1 pl-3"
          style={{ backgroundColor: 'color-mix(in oklab, var(--c-crit) 9%, transparent)' }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <Metric
              label="auth_failed"
              value={formatInt(auth ?? 0)}
              size="lg"
              tone="crit"
              note={
                <>
                  <span className="tnum">{formatPct(authShare, 0)}</span> de todas as rejeições
                </>
              }
            />
            <span className="text-micro uppercase tracking-label text-crit">
              sinal de segurança
            </span>
          </div>
        </div>

        <ul className="space-y-1">
          {others.map((bucket) => {
            const share = pctOf(bucket.count, total)
            const zero = bucket.count === 0
            const note = NOT_YET_IMPLEMENTED[bucket.reason]

            return (
              <li key={bucket.reason} className="flex items-center gap-2">
                <span
                  className={`min-w-0 flex-1 truncate font-mono text-sm ${
                    zero ? 'text-fg-muted' : 'text-fg-secondary'
                  }`}
                  title={
                    note ??
                    (bucket.uncatalogued
                      ? 'Motivo desconhecido'
                      : HUMAN[bucket.reason])
                  }
                >
                  {bucket.reason}
                  {note !== undefined && (
                    <span className="ml-1 text-micro text-fg-muted">(sem limitador hoje)</span>
                  )}
                </span>

                {/* Trilho de largura fixa: a barra é comparável entre linhas, e
                    a linha zerada mantém a altura — nada salta quando um motivo
                    sai do zero. */}
                <span aria-hidden="true" className="h-1 w-12 shrink-0 bg-inset">
                  <span className="block h-full bg-seq-3" style={{ width: `${share}%` }} />
                </span>

                <span
                  className={`w-14 shrink-0 text-right live-value font-mono text-sm ${
                    zero ? 'text-fg-muted' : 'text-fg'
                  }`}
                >
                  {formatInt(bucket.count)}
                </span>
              </li>
            )
          })}
        </ul>

        <div className="mt-auto border-t border-line-subtle pt-2">
          <Metric
            label="rejeitando agora"
            value={formatInt(rejectedPerSec)}
            unit="ev/s"
            tone={(rejectedPerSec ?? 0) > 0 ? 'warn' : 'neutral'}
          />
        </div>
      </div>
    </Panel>
  )
}
